import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Copy, ImagePlus, Loader2, Play, RefreshCw, Shapes, TriangleAlert, X } from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import { toast } from 'sonner'
import { compileGeomasterDsl, GeomasterCompileResponse, generateGeomasterDsl, GeomasterGenerateResponse } from '../api/geomaster'
import { getFileUrl, uploadFile } from '../api/qb'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Badge } from '../components/ui/badge'
import { copyToClipboard } from '../lib/utils'

const GEOGEBRA_DEPLOY_URL = 'https://www.geogebra.org/apps/deployggb.js'
const JSXGRAPH_CSS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jsxgraph/1.11.1/jsxgraph.css'
const JSXGRAPH_JS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jsxgraph/1.11.1/jsxgraphcore.js'

function escapeForInlineScript(value: string): string {
  return value.replace(/<\/script/gi, '<\\/script')
}

function buildGeogebraPreviewDoc(script: string): string {
  const escapedScript = escapeForInlineScript(script)
  return `<!DOCTYPE html>
<html lang="zh-CN">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: #0f172a; color: #e2e8f0; font-family: sans-serif; }
      #root { position: relative; width: 100%; height: 100%; }
      #ggb-element { width: 100%; height: 100%; min-height: 360px; background: #020617; }
      #status { position: absolute; left: 12px; bottom: 12px; z-index: 10; font-size: 12px; background: rgba(2, 6, 23, 0.78); border: 1px solid rgba(100, 116, 139, 0.45); border-radius: 8px; padding: 6px 10px; color: #cbd5e1; }
      #status.error { border-color: rgba(244, 63, 94, 0.55); color: #fecdd3; }
    </style>
    <script src="${GEOGEBRA_DEPLOY_URL}"></script>
  </head>
  <body>
    <div id="root">
      <div id="ggb-element"></div>
      <div id="status">GeoGebra 预览就绪</div>
    </div>
    <script>
      const statusEl = document.getElementById('status');
      const source = ${JSON.stringify(escapedScript)};
      const commands = source
        .split('\\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const applet = new GGBApplet({
        appName: "geometry",
        width: Math.max(window.innerWidth, 480),
        height: Math.max(window.innerHeight, 360),
        showToolBar: false,
        showAlgebraInput: false,
        showMenuBar: false,
        enableShiftDragZoom: true,
        useBrowserForJS: true,
        appletOnLoad(api) {
          let failed = 0;
          for (const command of commands) {
            try {
              api.evalCommand(command);
            } catch (error) {
              failed += 1;
            }
          }
          if (failed > 0) {
            statusEl.className = 'error';
            statusEl.textContent = '部分命令执行失败：' + failed + ' 条';
          } else {
            statusEl.className = '';
            statusEl.textContent = commands.length ? '命令执行完成：' + commands.length + ' 条' : '当前脚本为空';
          }
        }
      }, true);
      window.addEventListener('load', () => {
        try {
          applet.inject('ggb-element');
        } catch (error) {
          statusEl.className = 'error';
          statusEl.textContent = 'GeoGebra 初始化失败';
        }
      });
    </script>
  </body>
</html>`
}

function buildJsxGraphPreviewDoc(script: string): string {
  const escapedScript = escapeForInlineScript(script)
  return `<!DOCTYPE html>
<html lang="zh-CN">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <link rel="stylesheet" href="${JSXGRAPH_CSS_URL}" />
    <style>
      html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: #0f172a; color: #e2e8f0; font-family: sans-serif; }
      #root { position: relative; width: 100%; height: 100%; }
      #jxgbox { width: 100%; height: 100%; min-height: 360px; background: #020617; }
      #status { position: absolute; left: 12px; bottom: 12px; z-index: 10; font-size: 12px; background: rgba(2, 6, 23, 0.78); border: 1px solid rgba(100, 116, 139, 0.45); border-radius: 8px; padding: 6px 10px; color: #cbd5e1; }
      #status.error { border-color: rgba(244, 63, 94, 0.55); color: #fecdd3; }
    </style>
    <script src="${JSXGRAPH_JS_URL}"></script>
  </head>
  <body>
    <div id="root">
      <div id="jxgbox"></div>
      <div id="status">JSXGraph 预览就绪</div>
    </div>
    <script>
      const statusEl = document.getElementById('status');
      try {
        const source = ${JSON.stringify(escapedScript)};
        (new Function(source))();
        statusEl.className = '';
        statusEl.textContent = source.trim() ? '脚本执行完成' : '当前脚本为空';
      } catch (error) {
        statusEl.className = 'error';
        statusEl.textContent = '脚本执行失败：' + (error && error.message ? error.message : '未知错误');
      }
    </script>
  </body>
</html>`
}

export default function GeomasterStudioPage() {
  const navigate = useNavigate()
  const [problemText, setProblemText] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [modelName, setModelName] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [temperature, setTemperature] = useState('0.1')
  const [maxRepairRounds, setMaxRepairRounds] = useState('1')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<GeomasterGenerateResponse | null>(null)
  const [dslText, setDslText] = useState('')
  const [compileResult, setCompileResult] = useState<GeomasterCompileResponse | null>(null)
  const [compileLoading, setCompileLoading] = useState(false)
  const [activeCompiler, setActiveCompiler] = useState<'geogebra' | 'jsxgraph'>('geogebra')
  const [lastError, setLastError] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [imageName, setImageName] = useState('')
  const imageInputRef = useRef<HTMLInputElement>(null)

  const canSubmit = useMemo(() => {
    return Boolean(problemText.trim()) || Boolean(imageUrl.trim())
  }, [problemText, imageUrl])

  const semanticCount = useMemo(() => {
    return Array.isArray(result?.dsl?.semantic?.entities) ? result?.dsl?.semantic?.entities?.length : 0
  }, [result])

  const constructionCount = useMemo(() => {
    return Array.isArray(result?.dsl?.construction?.steps) ? result?.dsl?.construction?.steps?.length : 0
  }, [result])

  const timelineCount = useMemo(() => {
    return Array.isArray(result?.dsl?.render?.timeline?.steps) ? result?.dsl?.render?.timeline?.steps?.length : 0
  }, [result])

  const handleGenerate = async () => {
    if (!canSubmit) {
      toast.error('请至少输入题目文本或图片 URL')
      return
    }
    setLoading(true)
    setLastError('')
    try {
      const parsedTemp = Number(temperature)
      const parsedRounds = Number(maxRepairRounds)
      const payload = {
        problem_text: problemText.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
        llm: {
          model_name: modelName.trim() || undefined,
          base_url: baseUrl.trim() || undefined,
          temperature: Number.isFinite(parsedTemp) ? parsedTemp : undefined,
          max_repair_rounds: Number.isFinite(parsedRounds) ? parsedRounds : undefined,
        },
      }
      const response = await generateGeomasterDsl(payload)
      setResult(response)
      setDslText(JSON.stringify(response.dsl, null, 2))
      setCompileResult(null)
      if (response.validation.valid) {
        toast.success('Geomaster DSL 生成成功')
      } else {
        toast.warning('已生成 DSL，但存在校验问题')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '生成失败'
      setLastError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件')
      return
    }
    let preview: string | null = null
    setUploadingImage(true)
    try {
      preview = URL.createObjectURL(file)
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl)
      }
      setImagePreviewUrl(preview)
      setImageName(file.name)
      const uploaded = await uploadFile(file)
      const resolvedUrl = uploaded.url ? getFileUrl(uploaded.url) : getFileUrl(uploaded.file_id)
      if (resolvedUrl.startsWith('blob:')) {
        throw new Error('图片地址仍是本地预览地址')
      }
      setImageUrl(resolvedUrl)
      toast.success('图片上传成功，已回填图片 URL')
    } catch (error) {
      if (preview) {
        URL.revokeObjectURL(preview)
      }
      setImagePreviewUrl(null)
      setImageName('')
      setImageUrl('')
      const message = error instanceof Error ? error.message : '上传失败'
      toast.error(message)
    } finally {
      setUploadingImage(false)
    }
  }

  const handleRemoveImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }
    setImagePreviewUrl(null)
    setImageName('')
    setImageUrl('')
  }

  const handleCopyDsl = async () => {
    if (!dslText) {
      return
    }
    const copied = await copyToClipboard(dslText)
    if (copied) {
      toast.success('DSL 已复制')
      return
    }
    toast.error('复制失败')
  }

  const handleCompile = async () => {
    if (!dslText.trim()) {
      toast.error('请先生成或输入 DSL JSON')
      return
    }
    setCompileLoading(true)
    try {
      const dsl = JSON.parse(dslText)
      const compiled = await compileGeomasterDsl(dsl)
      setCompileResult(compiled)
      toast.success('已生成 GeoGebra 与 JSXGraph 脚本')
    } catch (error) {
      const message = error instanceof Error ? error.message : '编译失败'
      toast.error(message)
    } finally {
      setCompileLoading(false)
    }
  }

  const activeScript = activeCompiler === 'geogebra' ? compileResult?.geogebra?.script || '' : compileResult?.jsxgraph?.script || ''
  const activeDiagnostics = activeCompiler === 'geogebra' ? compileResult?.geogebra?.diagnostics || [] : compileResult?.jsxgraph?.diagnostics || []
  const geogebraPreviewDoc = useMemo(() => buildGeogebraPreviewDoc(compileResult?.geogebra?.script || ''), [compileResult?.geogebra?.script])
  const jsxgraphPreviewDoc = useMemo(() => buildJsxGraphPreviewDoc(compileResult?.jsxgraph?.script || ''), [compileResult?.jsxgraph?.script])
  const activePreviewDoc = activeCompiler === 'geogebra' ? geogebraPreviewDoc : jsxgraphPreviewDoc

  const handleCopyCompiled = async () => {
    if (!activeScript) {
      toast.error('暂无可复制脚本')
      return
    }
    const copied = await copyToClipboard(activeScript)
    if (copied) {
      toast.success('脚本已复制')
      return
    }
    toast.error('复制失败')
  }

  const handleFormatDsl = () => {
    if (!dslText.trim()) {
      return
    }
    try {
      const parsed = JSON.parse(dslText)
      setDslText(JSON.stringify(parsed, null, 2))
      toast.success('已格式化')
    } catch {
      toast.error('当前内容不是合法 JSON')
    }
  }

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl)
      }
    }
  }, [imagePreviewUrl])

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 lg:px-6">
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5">
        <div className="flex flex-col gap-3 rounded-2xl border border-cyan-500/20 bg-gradient-to-r from-slate-900 to-slate-900/60 p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-cyan-300">
              <Shapes className="h-4 w-4" />
              <span className="text-sm">Geomaster Studio</span>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">题目到 DSL 一体化工作台</h1>
            <p className="text-sm text-slate-400">输入题目与模型参数，生成 DSL 并实时查看 Schema 校验结果</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" onClick={() => navigate(ROUTES.HOME)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              返回首页
            </Button>
            <Button className="bg-cyan-600 text-white hover:bg-cyan-500" disabled={loading || !canSubmit} onClick={handleGenerate}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  生成中
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  生成 DSL
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <Card className="border-slate-800 bg-slate-900/80 xl:col-span-4">
            <CardHeader>
              <CardTitle>输入与模型参数</CardTitle>
              <CardDescription>至少提供题目文本或图片 URL</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="text-sm text-slate-300">题目文本</div>
                <Textarea
                  value={problemText}
                  onChange={(event) => setProblemText(event.target.value)}
                  placeholder="例如：已知三角形 ABC 中，AB=AC，点 D 在 BC 上，求证 ∠BAD=∠DAC。"
                  className="min-h-[160px] border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm text-slate-300">图片 URL</div>
                <Input
                  value={imageUrl}
                  onChange={(event) => setImageUrl(event.target.value)}
                  placeholder="https://..."
                  className="border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                />
                <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                  disabled={loading || uploadingImage}
                  onClick={() => imageInputRef.current?.click()}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      上传中...
                    </>
                  ) : (
                    <>
                      <ImagePlus className="mr-2 h-4 w-4" />
                      本地上传图片
                    </>
                  )}
                </Button>
                {imagePreviewUrl ? (
                  <div className="space-y-2 rounded-md border border-slate-700 bg-slate-950 p-3">
                    <img src={imagePreviewUrl} alt="题目图片预览" className="max-h-52 w-full rounded-md object-contain" />
                    <div className="flex items-center justify-between text-xs text-slate-300">
                      <span className="truncate">{imageName}</span>
                      <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-slate-300 hover:bg-slate-800 hover:text-white" onClick={handleRemoveImage}>
                        <X className="mr-1 h-3 w-3" />
                        移除
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-sm text-slate-300">模型名</div>
                  <Input
                    value={modelName}
                    onChange={(event) => setModelName(event.target.value)}
                    placeholder="如 gpt-4o"
                    className="border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-slate-300">Base URL</div>
                  <Input
                    value={baseUrl}
                    onChange={(event) => setBaseUrl(event.target.value)}
                    placeholder="可选"
                    className="border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-slate-300">Temperature</div>
                  <Input
                    value={temperature}
                    onChange={(event) => setTemperature(event.target.value)}
                    placeholder="0~1"
                    className="border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-slate-300">修复轮次</div>
                  <Input
                    value={maxRepairRounds}
                    onChange={(event) => setMaxRepairRounds(event.target.value)}
                    placeholder="0~3"
                    className="border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                  />
                </div>
              </div>
              {lastError ? (
                <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
                  {lastError}
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/80 xl:col-span-5">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>DSL 结果</CardTitle>
                <CardDescription>可复制或手动格式化 JSON</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" onClick={handleFormatDsl}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  格式化
                </Button>
                <Button variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" onClick={handleCopyDsl}>
                  <Copy className="mr-1 h-3.5 w-3.5" />
                  复制
                </Button>
                <Button
                  size="sm"
                  className="bg-cyan-600 text-white hover:bg-cyan-500"
                  disabled={compileLoading || !dslText.trim()}
                  onClick={handleCompile}
                >
                  {compileLoading ? (
                    <>
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      编译中
                    </>
                  ) : (
                    '编译脚本'
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={dslText}
                onChange={(event) => setDslText(event.target.value)}
                placeholder="生成后将在这里显示 DSL JSON..."
                className="h-[560px] resize-none border-slate-700 bg-black/50 font-mono text-xs text-emerald-100 placeholder:text-slate-500"
              />
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/80 xl:col-span-3">
            <CardHeader>
              <CardTitle>质量与摘要</CardTitle>
              <CardDescription>Schema 校验与结构统计</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                {result?.validation.valid ? (
                  <Badge className="bg-emerald-600 text-white">
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                    Schema Pass
                  </Badge>
                ) : (
                  <Badge className="bg-rose-600 text-white">
                    <TriangleAlert className="mr-1 h-3.5 w-3.5" />
                    Schema Fail
                  </Badge>
                )}
                {typeof result?.validation.repair_rounds === 'number' ? (
                  <Badge variant="outline" className="border-slate-600 text-slate-300">
                    修复 {result.validation.repair_rounds} 轮
                  </Badge>
                ) : null}
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md border border-slate-700 bg-slate-950 p-2">
                  <div className="text-lg font-semibold text-cyan-300">{semanticCount}</div>
                  <div className="text-xs text-slate-400">实体</div>
                </div>
                <div className="rounded-md border border-slate-700 bg-slate-950 p-2">
                  <div className="text-lg font-semibold text-amber-300">{constructionCount}</div>
                  <div className="text-xs text-slate-400">构造步骤</div>
                </div>
                <div className="rounded-md border border-slate-700 bg-slate-950 p-2">
                  <div className="text-lg font-semibold text-violet-300">{timelineCount}</div>
                  <div className="text-xs text-slate-400">渲染步骤</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm text-slate-300">Warnings</div>
                <div className="max-h-28 overflow-auto rounded-md border border-slate-700 bg-slate-950 p-2 text-xs text-slate-300">
                  {result?.warnings?.length ? result.warnings.map((item, index) => <div key={`${item}-${index}`}>{item}</div>) : '无'}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm text-slate-300">Schema Errors</div>
                <div className="max-h-64 overflow-auto rounded-md border border-slate-700 bg-slate-950 p-2 text-xs text-rose-200">
                  {result?.validation?.errors?.length ? result.validation.errors.map((item, index) => <div key={`${item}-${index}`}>{item}</div>) : '无'}
                </div>
              </div>

              <div className="space-y-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-300">
                <div>模型：{result?.meta?.model_name || '默认配置'}</div>
                <div>版本：{result?.dsl?.version || '-'}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-800 bg-slate-900/80">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>DSL 编译输出</CardTitle>
              <CardDescription>支持导出与可视化 GeoGebra / JSXGraph 脚本</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={activeCompiler === 'geogebra' ? 'default' : 'outline'}
                size="sm"
                className={activeCompiler === 'geogebra' ? 'bg-emerald-600 text-white hover:bg-emerald-500' : 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'}
                onClick={() => setActiveCompiler('geogebra')}
              >
                GeoGebra
              </Button>
              <Button
                variant={activeCompiler === 'jsxgraph' ? 'default' : 'outline'}
                size="sm"
                className={activeCompiler === 'jsxgraph' ? 'bg-violet-600 text-white hover:bg-violet-500' : 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'}
                onClick={() => setActiveCompiler('jsxgraph')}
              >
                JSXGraph
              </Button>
              <Button variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" onClick={handleCopyCompiled}>
                <Copy className="mr-1 h-3.5 w-3.5" />
                复制脚本
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <div className="lg:col-span-3">
                <Textarea
                  value={activeScript}
                  readOnly
                  placeholder="点击“编译脚本”后显示目标引擎代码..."
                  className="h-[260px] resize-none border-slate-700 bg-black/50 font-mono text-xs text-cyan-100 placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2 rounded-md border border-slate-700 bg-slate-950 p-3">
                <div className="text-sm text-slate-300">编译诊断</div>
                <div className="max-h-[220px] overflow-auto text-xs text-slate-300">
                  {activeDiagnostics.length
                    ? activeDiagnostics.map((item, index) => <div key={`${item}-${index}`}>{item}</div>)
                    : '无'}
                </div>
              </div>
            </div>
            <div className="space-y-2 rounded-md border border-slate-700 bg-slate-950/60 p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-300">脚本可视化预览</div>
                <Badge variant="outline" className="border-slate-600 text-slate-200">
                  {activeCompiler === 'geogebra' ? 'GeoGebra' : 'JSXGraph'}
                </Badge>
              </div>
              {activeScript.trim() ? (
                <iframe
                  key={`${activeCompiler}-${activeScript}`}
                  title={`${activeCompiler}-preview`}
                  srcDoc={activePreviewDoc}
                  className="h-[420px] w-full rounded-md border border-slate-700 bg-slate-950"
                  sandbox="allow-scripts allow-same-origin"
                />
              ) : (
                <div className="flex h-[420px] items-center justify-center rounded-md border border-dashed border-slate-700 bg-slate-950 text-sm text-slate-400">
                  请先编译 DSL，再查看可视化结果
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
